export const ALARM_CLIP_SECONDS_PER_FRAME = 0.5;

/** Returns the length of one slideshow cycle at half a second per image. */
export function alarmClipDurationSeconds(frameCount: number): number {
  return Math.max(0, frameCount) * ALARM_CLIP_SECONDS_PER_FRAME;
}

/** Wraps elapsed clip time so slideshow playback can loop within one cycle. */
export function normalizeAlarmClipElapsedSeconds(
  elapsedSeconds: number,
  durationSeconds: number,
): number {
  const duration = Math.max(durationSeconds, 0.001);
  const nonNegative = Math.max(0, elapsedSeconds);
  if (nonNegative < duration) {
    return nonNegative;
  }
  return nonNegative % duration;
}

/** Maps elapsed clip time to a frame index across `frameCount` still images. */
export function resolveAlarmClipFrameIndex(
  elapsedSeconds: number,
  frameCount: number,
  durationSeconds: number = alarmClipDurationSeconds(frameCount),
): number {
  if (frameCount <= 0) {
    return 0;
  }

  const duration = Math.max(durationSeconds, 0.001);
  const looped = normalizeAlarmClipElapsedSeconds(elapsedSeconds, duration);
  const index = Math.floor((looped / duration) * frameCount + 1e-9);
  return Math.min(Math.max(0, index), frameCount - 1);
}

/** Nested `detectedObjects` bands: group 0 → frames 1–3, 1 → 4–7, 2 → 8–10 (0-based). */
const ALARM_CLIP_DETECTION_FRAME_BANDS: ReadonlyArray<readonly [number, number]> = [
  [0, 2],
  [3, 6],
  [7, 9],
];

/** Returns the nested detection group for a 0-based clip frame, or null outside bands 1–10. */
export function alarmClipFrameGroupIndex(frameIndex: number): number | null {
  for (let group = 0; group < ALARM_CLIP_DETECTION_FRAME_BANDS.length; group++) {
    const [start, end] = ALARM_CLIP_DETECTION_FRAME_BANDS[group]!;
    if (frameIndex >= start && frameIndex <= end) {
      return group;
    }
  }
  return null;
}

/** Frame indexes (0-based) covered by a nested detection group, clipped to `frameCount`. */
export function alarmClipFrameIndexesForGroup(groupIndex: number, frameCount: number): number[] {
  const band = ALARM_CLIP_DETECTION_FRAME_BANDS[groupIndex];
  if (!band || frameCount <= 0) {
    return [];
  }

  const [start, end] = band;
  const frames: number[] = [];
  for (let frame = start; frame <= end && frame < frameCount; frame++) {
    frames.push(frame);
  }
  return frames;
}
